import { Link } from "@tanstack/react-router";
import {
  PanelLeft,
  ArrowUpRight,
  Share2,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Thread } from "@/types/ai-chat";
import { Logo } from "@/components/common/Logo";

const CATEGORY_COLORS: Record<string, string> = {
  "kra-registration": "#6B1A2A",
  "tax-returns": "#1D4ED8",
  etims: "#0F6E56",
  bookkeeping: "#7C3AED",
  advisory: "#D97706",
  audit: "#DC2626",
  international: "#0369A1",
  training: "#16A34A",
  general: "#6B7280",
};

function ThreadBadge({ category }: { category: string }) {
  const color = CATEGORY_COLORS[category] ?? "#6B7280";
  const label = category.replace(/-/g, " ");
  return (
    <span
      className="inline-flex items-center text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
      style={{ background: `${color}18`, color }}
    >
      {label}
    </span>
  );
}

interface ChatHeaderProps {
  activeThread: Thread | null;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  ctaHref?: string;
  ctaLabel?: string;
  className?: string;
}

export function ChatHeader({
  activeThread,
  sidebarOpen,
  onToggleSidebar,
  ctaHref = "/contact",
  ctaLabel = "Talk to a CPA",
  className,
}: ChatHeaderProps) {
  return (
    <header
      className={cn(
        "flex items-center justify-between px-3 sm:px-4 py-2.5 border-b border-[var(--color-border)] bg-white flex-shrink-0",
        className,
      )}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8  flex-shrink-0"
                onClick={onToggleSidebar}
                aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              >
                <PanelLeft size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {sidebarOpen ? "Close sidebar" : "Open sidebar"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {!sidebarOpen && (
          <>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0">
              <Logo variant="light" mode="mark" size="md" />
            </div>
            <Separator orientation="vertical" className="h-5" />
          </>
        )}

        <div className="min-w-0">
          <h1 className="text-[13.5px] font-bold text-[var(--color-charcoal)] leading-tight truncate">
            {activeThread ? activeThread.title : "New Conversation"}
          </h1>
          {activeThread && (
            <div className="flex items-center gap-2 mt-0.5">
              <ThreadBadge category={activeThread.category} />
              <span className="text-[10px] ">
                {activeThread.messageCount} messages
              </span>
            </div>
          )}
          {!activeThread && (
            <p className="text-[10.5px]  flex items-center gap-1">
              Powered by Rosewood AI
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        <TooltipProvider delayDuration={300}>
          {activeThread && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 "
                    aria-label="Share conversation"
                  >
                    <Share2 size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Share conversation</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 "
                    aria-label="Export conversation"
                  >
                    <Download size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export conversation</TooltipContent>
              </Tooltip>

              <Separator orientation="vertical" className="h-5 mx-1" />
            </>
          )}

          <Button
            size="sm"
            className="gradient-burgundy text-white text-[11.5px] font-semibold gap-1.5 hover:opacity-90 h-8 px-3"
            asChild
          >
            <Link to={ctaHref}>
              <ArrowUpRight size={12} />
              <span className="hidden sm:inline">{ctaLabel}</span>
              <span className="sm:hidden">CPA</span>
            </Link>
          </Button>
        </TooltipProvider>
      </div>
    </header>
  );
}
